import React from "react";
import { useSelector, useDispatch } from "react-redux";

import { makeStyles } from "@material-ui/styles";
import Divider from "@material-ui/core/Divider";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SaveIcon from "@material-ui/icons/Save";
import ViewIcon from "@material-ui/icons/RemoveRedEye";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import Typography from "@material-ui/core/Typography";
import { Card, Drawer } from "@material-ui/core";

import { MuiTable } from "@jazasoft/mui-table";
import { PageFooter, Button, WithReduxForm, TextInput, showNotification } from "jazasoft";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import CardHeader from "../../../components/CardHeader";
import useSafeState from "../../../hooks/useSafeState";
import { AlertParemeter, OState, Role } from "../../../utils/types";
import { useDeepMemo, useMutation, useQuery, useTenant } from "../../../hooks";
import { inputOptions, isEmpty, toFixed, toFixedSigned } from "../../../utils/helpers";
import { choicesParameter } from "../cutPlanHelper";

const useStyles = makeStyles(theme => ({
  nowrapTable: {
    whiteSpace: "pre"
  },
  drawerPaper: {
    maxWidth: 1000,
    minWidth: 800
  },
  drawerHeader: {
    display: "flex",
    width: props => props.drawerWidth || 780,
    alignItems: "center",
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  expansionContent: {
    overflowX: "auto",
    flexDirection: "column",
    padding: 0
  },
  pageFooter: {
    padding: "1em"
  }
}));

export const PARAMETER_CONFIG = {
  cutPlanQty: {
    getValues: (varianceAnalysis, alertParamList) => {
      const booking = varianceAnalysis?.orderQty || 0;
      const actual = varianceAnalysis?.plannedQty || 0;
      const diff = actual - booking;
      const diffPercent = booking > 0 ? (diff * 100) / booking : 0.0;
      const diffPercentLabel = booking > 0 ? ` (${toFixedSigned((diff * 100) / booking, 1)}%)` : "";
      const extraShipPercent = varianceAnalysis.extraShipPercent || 0;
      const shortShipPercent = varianceAnalysis.shortShipPercent || 0;

      return {
        booking,
        actual,
        diff: `${toFixedSigned(actual - booking, 0)}${diffPercentLabel}`,
        impact: diff > 0 ? (diffPercent > extraShipPercent ? "Increase" : "") : -diffPercent > shortShipPercent ? "Decrease" : ""
      };
    }
  },

  consumptionVariation: {
    getValues: (va, alertParamList, _, tenantId) => {
      const alertParam = alertParamList?.find(e => e.parameter === AlertParemeter.CONSUMPTION_VARIATION.id);
      const plusTolerance = alertParam?.plusTolerance || 1000;
      const minusTolerance = alertParam?.minusTolerance || -1000;

      const booking = va?.bomCu || 0;
      const actual = va?.actualCu || 0;
      const stdCu = va?.stdCu || 0;
      const finalActual = tenantId === "aquarelle" || tenantId === "aqtest" ? stdCu : actual; // For Aquarelle, show variance based on std cu instead of actual cu
      const diffInCm = (finalActual - booking) * 100;
      const diffPercent = booking > 0 ? ((finalActual - booking) * 100) / booking : undefined;
      const impact = isEmpty(diffPercent)
        ? ""
        : diffPercent > 0
          ? diffPercent > plusTolerance
            ? "Increase"
            : ""
          : -diffPercent > -minusTolerance
            ? "Decrease"
            : "";
      return {
        booking: `${booking} m`,
        actual: `${toFixed(finalActual, 3)} m`,
        diff: `${toFixedSigned(diffInCm)} cm`,
        diffInCm,
        impact
      };
    }
  },

  markerEfficiencyVariation: {
    getValues: (va, alertParamList, cutOrder) => {
      const alertParam = alertParamList?.find(e => e.parameter === AlertParemeter.MARKER_EFFICIENCY_VARIATION.id);
      const plusTolerance = alertParam?.plusTolerance || 1000;
      const minusTolerance = alertParam?.minusTolerance || -1000;

      const booking = cutOrder?.orderConfig?.targetMarkerEfficiency || 0;
      const actual = toFixed(va?.markerEfficiency || 0, 2);
      const diff = actual - booking;
      const impact = isEmpty(cutOrder?.orderConfig?.targetMarkerEfficiency)
        ? ""
        : diff > 0
          ? diff > plusTolerance
            ? "Increase"
            : ""
          : -diff > -minusTolerance
            ? "Decrease"
            : "";

      return {
        booking: booking > 0 ? `${booking}` : "",
        actual: `${actual}%`,
        diff: booking > 0 ? `${toFixedSigned(diff)}%` : "-",
        impact
      };
    }
  },

  cuttableWidthVariation: {
    getValues: (va, alertParamList) => {
      const alertParam = alertParamList?.find(e => e.parameter === AlertParemeter.MARKER_EFFICIENCY_VARIATION.id);
      const plusTolerance = alertParam?.plusTolerance || 1000;
      const minusTolerance = alertParam?.minusTolerance || -1000;

      const booking = va?.orderingWidth || 0;
      const actual = toFixed(va?.actualWidth || 0, 2);
      const diff = actual - booking;
      const diffPercent = booking > 0 ? (diff * 100) / booking : undefined;
      const diffPercentLabel = booking > 0 ? ` (${toFixedSigned((diff * 100) / booking, 1)}%)` : "";
      const impact = isEmpty(diffPercent)
        ? ""
        : diffPercent > 0
          ? diffPercent > plusTolerance
            ? "Increase"
            : ""
          : -diffPercent > -minusTolerance
            ? "Decrease"
            : "";

      return {
        booking: booking > 0 ? `${booking} cm` : "",
        actual: `${actual} cm`,
        diff: booking > 0 ? `${toFixedSigned(diff)} cm${diffPercentLabel}` : "-",
        impact
      };
    }
  },

  gsmVariation: {
    getValues: (va, alertParamList) => {
      const alertParam = alertParamList?.find(e => e.parameter === AlertParemeter.MARKER_EFFICIENCY_VARIATION.id);
      const plusTolerance = alertParam?.plusTolerance || 1000;
      const minusTolerance = alertParam?.minusTolerance || -1000;
      const booking = va?.orderingGsm || 0;
      const actual = va?.actualGsm || 0;
      const diff = actual - booking;
      const diffPercent = booking > 0 ? (diff * 100) / booking : undefined;
      const diffPercentLabel = booking > 0 ? ` (${toFixedSigned((diff * 100) / booking, 1)}%)` : "";
      const impact = isEmpty(diffPercent)
        ? ""
        : diffPercent > 0
          ? diffPercent > plusTolerance
            ? "Increase"
            : ""
          : -diffPercent > -minusTolerance
            ? "Decrease"
            : "";

      return {
        booking: booking > 0 ? booking : "",
        actual: actual > 0 ? actual : "-",
        diff: booking > 0 || actual > 0 ? `${diff}${diffPercentLabel}` : "-",
        impact
      };
    }
  },

  csvVariation: {
    getValues: (va, alertParamList) => {
      return {
        booking: va?.orderingCsv || "",
        actual: va?.csv || "-",
        diff: !isEmpty(va?.orderingCsv) && va?.orderingCsv !== va?.csv ? "Changed" : "-",
        impact: !isEmpty(va?.orderingCsv) && va?.orderingCsv !== va?.csv ? "Increase" : ""
      };
    }
  },

  warpShrinkageVariation: {
    getValues: (va, alertParamList) => {
      const alertParam = alertParamList?.find(e => e.parameter === AlertParemeter.MARKER_EFFICIENCY_VARIATION.id);
      const plusTolerance = alertParam?.plusTolerance || 1000;
      const minusTolerance = alertParam?.minusTolerance || -1000;

      const booking = !isEmpty(va?.orderingWarpShrinkage) ? toFixed(va?.orderingWarpShrinkage) : undefined;
      const actual = !isEmpty(va?.actualWarpShrinkage) ? toFixed(va?.actualWarpShrinkage || 0) : undefined;
      const diff = !isEmpty(actual) && !isEmpty(booking) ? actual - booking : undefined;

      const impact = isEmpty(diff) ? "" : diff > 0 ? (diff > plusTolerance ? "Increase" : "") : -diff > -minusTolerance ? "Decrease" : "";

      return {
        booking: !isEmpty(booking) ? `${booking}%` : "",
        actual: !isEmpty(actual) ? `${actual}%` : "-",
        diff: !isEmpty(diff) ? `${toFixedSigned(diff)}%` : "-",
        impact
      };
    }
  },

  weftShrinkageVariation: {
    getValues: (va, alertParamList) => {
      const alertParam = alertParamList?.find(e => e.parameter === AlertParemeter.MARKER_EFFICIENCY_VARIATION.id);
      const plusTolerance = alertParam?.plusTolerance || 1000;
      const minusTolerance = alertParam?.minusTolerance || -1000;

      const booking = !isEmpty(va?.orderingWeftShrinkage) ? toFixed(va?.orderingWeftShrinkage) : undefined;
      const actual = !isEmpty(va?.actualWeftShrinkage) ? toFixed(va?.actualWeftShrinkage || 0) : undefined;
      const diff = !isEmpty(actual) && !isEmpty(booking) ? actual - booking : undefined;

      const impact = isEmpty(diff) ? "" : diff > 0 ? (diff > plusTolerance ? "Increase" : "") : -diff > -minusTolerance ? "Decrease" : "";

      return {
        booking: !isEmpty(booking) ? `${booking}%` : "",
        actual: !isEmpty(actual) ? `${actual}%` : "-",
        diff: !isEmpty(diff) ? `${toFixedSigned(diff)}%` : "-",
        impact
      };
    }
  }
};

const VarianceReport = props => {
  const classes = useStyles();
  const { className, roles, order, cutOrder, rollInfoList, onRefresh } = props;
  const dispatch = useDispatch();
  const [mutate, { loading }] = useMutation();
  const tenant = useTenant();

  const { data: alertParameterList = [] } = useQuery({ url: "alert-parameters", params: { size: 1000 } });
  const formValues = useSelector(state => state?.form && state.form["record-form"]?.values);

  const isMerchant = roles?.includes(Role.MERCHANT);
  const isCutPlanner = roles?.includes(Role.CUT_PLANNER);
  const isSuperUser = roles?.includes(Role.SUPER_USER);

  const [expanded, setExpanded] = useSafeState(false);
  const [drawerState, setDrawerState] = useSafeState({ open: false, name: undefined }); // name: alert_reason, cu_contribution

  const alertReasons = JSON.parse(cutOrder?.orderConfig?.alerts || "[]") || [];

  const onDrawerClose = () => {
    setDrawerState({ open: false });
  };

  const onSubmit = (rows, _, onSubmitComplete) => {
    const isEfficiencyUpdate = isEmpty(cutOrder?.orderConfig?.targetMarkerEfficiency);
    if (!isEfficiencyUpdate && isCutPlanner && isEmpty(formValues["plannerRemarks"])) {
      dispatch(showNotification(`Planner Remarks is required.`, { type: "danger" }));
      onSubmitComplete && onSubmitComplete(rows);
      return;
    }
    if (!isEfficiencyUpdate && isMerchant && isEmpty(formValues["merchantRemarks"])) {
      dispatch(showNotification(`Mrechant Remarks is required.`, { type: "danger" }));
      onSubmitComplete && onSubmitComplete(rows);
      return;
    }

    const markerRow = rows?.find(r => r.parameter === AlertParemeter.MARKER_EFFICIENCY_VARIATION.id);

    if (isEmpty(markerRow?.booking) || markerRow?.booking === 0) {
      dispatch(showNotification(`Target Marker Efficiency is required.`, { type: "danger" }));
      onSubmitComplete && onSubmitComplete(rows);
      return;
    }

    const invalidRow = rows.filter(row => row.impact !== "").find(row => isEmpty(row.reasons));

    if (!isEfficiencyUpdate && invalidRow) {
      dispatch(showNotification(`Reasons is required for Parameter - ${invalidRow.parameterName}`, { type: "danger" }));
      onSubmitComplete && onSubmitComplete(rows);
      return;
    }

    const alertList = isEmpty(cutOrder?.orderConfig?.alerts) ? [] : JSON.parse(cutOrder?.orderConfig?.alerts);

    const alertsPayload = !isEfficiencyUpdate
      ? rows
          .filter(row => row.impact !== "")
          .map(row => {
            const alert = alertList?.find(e => e.parameter === row.parameter) || {};
            const alertParameter = choicesParameter?.find(p => p.id === row.parameter);

            if (!alertParameter) {
              console.warn(`Parameter mapping missing for: ${row.parameterName}`);
            }

            return {
              ...alert,
              parameter: alertParameter?.id || null,
              reasons: Array.isArray(row.reasons)
                ? row.reasons
                : row.reasons
                    ?.split(",")
                    ?.map(r => r.trim())
                    .filter(Boolean) || []
            };
          })
      : [];

    const planningDto = {
      cadRemarks: formValues?.plannerRemarks,
      merchantRemarks: formValues?.merchantRemarks,
      targetMarkerEfficiency: rows?.find(r => r.parameter === AlertParemeter.MARKER_EFFICIENCY_VARIATION.id)?.booking || 0,
      alerts: JSON.stringify(alertsPayload),
      cutPlanAnalysisCompleted: !isEfficiencyUpdate ? true : false
    };

    const options = {
      url: `orders/${order?.id}/markers/approval`,
      method: "put",
      data: planningDto,
      params: { action: "default", flowId: cutOrder.id }
    };
    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("Alert Reasons updated successfully."));
        onDrawerClose();
        onRefresh && onRefresh();
      }
    });

    onSubmitComplete && onSubmitComplete(rows);
  };

  const hasWeight = rollInfoList?.some(r => !isEmpty(r.weight));
  const wash = cutOrder?.groupList?.some(g => !isEmpty(g.warpPattern));

  const onVarianceClick = () => {
    setDrawerState({ open: true, name: "alert_reason" });
  };

  const rows = useDeepMemo(() => {
    let finalParameters = choicesParameter;
    if (!hasWeight) {
      finalParameters = finalParameters?.filter(p => p.id !== AlertParemeter.GSM_VARIATION.id);
    }
    if (!wash) {
      finalParameters = finalParameters?.filter(
        p => ![AlertParemeter.WARP_SHRINKAGE_VARIATION.id, AlertParemeter.WEFT_SHRINKAGE_VARIATION.id].includes(p.id)
      );
    }

    const varianceAnalysisInfo = JSON.parse(cutOrder?.orderConfig?.varianceAnalysisInfo || "[]") || [];
    const varianceAnalysis = varianceAnalysisInfo[0] || {};

    const rowList = finalParameters.map((parameter, idx) => {
      const alertParameter = Array.isArray(alertParameterList) ? alertParameterList?.find(ap => ap.parameter === parameter.id) || {} : {};
      const config = PARAMETER_CONFIG[parameter.id];

      const reasonList = alertReasons?.find(ar => ar.parameter === parameter?.id)?.reasons || [];
      if (config) {
        const values = config.getValues(varianceAnalysis, alertParameterList, cutOrder, tenant?.tenantId);
        return {
          ...alertParameter,
          ...values,
          serialNo: idx + 1,
          parameterName: parameter.name,
          reasons: reasonList
        };
      }

      return {
        ...alertParameter,
        serialNo: idx + 1,
        parameterName: parameter.name,
        reasons: reasonList
      };
    });

    return rowList;
  }, [cutOrder, tenant]);

  const columns = useDeepMemo(() => {
    const fetchChoices = ({ row }) => {
      const alertParameter = Array.isArray(alertParameterList) ? alertParameterList?.find(p => row.parameter === p.parameter) : {};
      return alertParameter?.alertParameterItems?.filter(api => api.impact === row.impact).map(api => ({ id: api.reason, name: api.reason }));
    };
    let columnList = [
      { dataKey: "serialNo", title: "S No", align: "center" },
      { dataKey: "parameterName", title: "Parameter" },
      {
        dataKey: "booking",
        title: "Booking/Target",
        align: "right",
        inputType: isSuperUser || isCutPlanner ? "text-input" : undefined,
        disabled: (row, dataKey) => row?.parameter !== AlertParemeter.MARKER_EFFICIENCY_VARIATION.id,
        render: (value, _, row) => (row?.parameter === AlertParemeter.MARKER_EFFICIENCY_VARIATION.id ? (value ? `${value}%` : "-") : value || "-")
      },
      { dataKey: "actual", title: "Actual", align: "right" },
      {
        dataKey: "diff",
        title: "Difference",
        align: "right",
        render: (value, _, row) => (
          <span
            style={{
              backgroundColor: isEmpty(row.impact) ? undefined : "orange",
              color: isEmpty(row.impact) ? undefined : "white",
              padding: isEmpty(row.impact) ? undefined : "2px 4px",
              borderRadius: isEmpty(row.impact) ? undefined : 4
            }}>
            {value}
          </span>
        )
      },
      {
        dataKey: "reasons",
        title: "Reasons",
        inputType: "select-input",
        options: { multiple: true },
        render: value => (Array.isArray(value) ? value.join(", ") : value),
        disabled: (row, dataKey) => isEmpty(cutOrder?.orderConfig?.targetMarkerEfficiency) || row?.impact === "",
        choices: fetchChoices
      }
    ];

    return columnList;
  }, [hasWeight, rows, alertParameterList]);

  const inlineActions = useDeepMemo(() => {
    if (tenant?.tenantId === "aquarelle" || tenant?.tenantId === "aqtest") {
      return row =>
        row.parameter !== AlertParemeter.CONSUMPTION_VARIATION.id
          ? []
          : [{ name: "update_contrib", icon: <ViewIcon />, tooltip: "Contribution Info" }];
    }
    return [];
  }, [tenant]);

  const onInlineActionClick = (event, action, row, onActionComplete) => {
    if (isEmpty(row.reasons)) {
      dispatch(showNotification("Reason is missing. Hint: Update Reason first", { type: "danger" }));
      onActionComplete && onActionComplete();
      return;
    }
    setDrawerState({ open: true, name: "cu_contribution", cuDiff: row.diffInCm, reasonList: row.reasons || [] });
    onActionComplete && onActionComplete();
  };

  if (cutOrder?.stateValue <= OState.MAIN_CUTPLAN_GENERATED.stateValue) {
    return <div />;
  }

  return (
    <>
      <AlertReasonDrawer
        open={drawerState.open && drawerState.name === "alert_reason"}
        cutOrder={cutOrder}
        columns={columns}
        rows={rows}
        loading={loading}
        onSubmit={onSubmit}
        isSuperUser={isSuperUser}
        isMerchant={isMerchant}
        onDrawerClose={onDrawerClose}
      />

      <ContributionDrawer
        open={drawerState.open && drawerState.name === "cu_contribution"}
        cuDiff={drawerState.cuDiff}
        orderId={order?.id}
        cutOrder={cutOrder}
        reasonList={drawerState.reasonList}
        onSubmit={onSubmit}
        onDrawerClose={onDrawerClose}
        onRefresh={onRefresh}
      />

      <Accordion className={className} expanded={expanded} onChange={(e, expanded) => setExpanded(expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <CardHeader title="Variance Report" style={{ margin: 0, padding: "0.5em" }} />
        </AccordionSummary>
        {/* <Divider /> */}
        <AccordionDetails className={classes.expansionContent}>
          <Divider />
          <PerfectScrollbar style={{ padding: "1.5em" }}>
            <MuiTable
              classes={{ table: classes.nowrapTable }}
              columns={columns}
              rows={rows}
              cellLength={100}
              fontSize={16}
              headerCellStyle={{ fontWeight: "bold" }}
              inlineActions={inlineActions}
              onInlineActionClick={onInlineActionClick}
            />

            <div style={{ paddingTop: 16, paddingLeft: 4 }}>
              <Typography variant="h6">Planner Remarks</Typography>
              <div style={{ padding: "8px 0" }}>
                <span style={{ fontSize: 12 }}>{cutOrder?.orderConfig?.cadRemarks ? cutOrder?.orderConfig?.cadRemarks : "-"}</span>
              </div>
            </div>
            <div style={{ paddingTop: 16, paddingLeft: 4 }}>
              <Typography variant="h6">{`Merchant Remarks ${cutOrder?.orderConfig?.cutPlanApprovedBy ? `(${cutOrder?.orderConfig?.cutPlanApprovedBy})` : "(Not Approved)"}`}</Typography>
              <div style={{ padding: "8px 0" }}>
                <span style={{ fontSize: 12 }}>{cutOrder?.orderConfig?.merchantRemarks ? cutOrder?.orderConfig?.merchantRemarks : "-"}</span>
              </div>
            </div>
          </PerfectScrollbar>
          <Divider />

          {cutOrder?.stateValue <= OState.CLOSURE_CONFIRMATION_PENDING.stateValue && (
            <div style={{ display: "flex", justifyContent: "flex-end", padding: 8 }}>
              <Button label="Update" onClick={onVarianceClick} variant="outlined" />
            </div>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default VarianceReport;

const AlertReasonDrawer = ({ open, cutOrder, columns, rows, loading, onSubmit, isSuperUser, isMerchant, onDrawerClose }) => {
  const classes = useStyles();

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  return (
    <Drawer open={open || false} anchor="right" classes={{ paper: classes.drawerPaper }} onClose={onDrawerClose}>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onDrawerClose}>
          <ChevronRightIcon />
        </IconButton>
        <div>
          <Typography variant="h5">{"Variance Report"}</Typography>
        </div>
      </div>
      <Divider />

      <PerfectScrollbar>
        <WithReduxForm
          initialValues={{
            plannerRemarks: cutOrder?.orderConfig?.cadRemarks || "",
            merchantRemarks: cutOrder?.orderConfig?.merchantRemarks || ""
          }}>
          {({ handleSubmit }) => (
            <Card style={{ padding: 8 }}>
              <MuiTable
                classes={{ table: classes.nowrapTable }}
                columns={columns}
                rows={rows}
                editable
                editing
                handleSubmitRef={handleSubmitRef}
                onSubmit={onSubmit}
                fontSize={16}
                headerCellStyle={{ fontWeight: "bold" }}
                cellLength={100}
              />

              {/* Planner Remarks */}
              <div style={{ paddingLeft: 8, paddingRight: 8, paddingTop: 4 }}>
                <TextInput
                  source="plannerRemarks"
                  disabled={isMerchant || isEmpty(cutOrder?.orderConfig?.targetMarkerEfficiency)}
                  fullWidth
                  {...inputOptions(3)}
                />
              </div>

              {/* Mrechant Remarks */}
              {(isSuperUser || isMerchant) && (
                <div style={{ paddingLeft: 8, paddingRight: 8, paddingTop: 4 }}>
                  <TextInput
                    source="merchantRemarks"
                    disabled={isEmpty(cutOrder?.orderConfig?.targetMarkerEfficiency)}
                    fullWidth
                    {...inputOptions(3)}
                  />
                </div>
              )}
              <Divider />

              <PageFooter className={classes.pageFooter}>
                <Button
                  label="Save"
                  color="primary"
                  variant="contained"
                  disabled={loading}
                  onClick={event => {
                    handleSubmit(() => {})();
                    ffHandleSubmit(event);
                  }}>
                  <SaveIcon />
                </Button>

                <Button label="Close" variant="outlined" color="primary" style={{ marginLeft: "1.5em" }} onClick={onDrawerClose}>
                  <CloseIcon />
                </Button>
              </PageFooter>
            </Card>
          )}
        </WithReduxForm>
      </PerfectScrollbar>
    </Drawer>
  );
};

const ContributionDrawer = ({ open, orderId, cutOrder, cuDiff, reasonList, onDrawerClose, onRefresh }) => {
  const classes = useStyles();

  const [mutate] = useMutation();
  const dispatch = useDispatch();

  const columns = useDeepMemo(
    () => [
      { dataKey: "serial", title: "SL No" },
      { dataKey: "reason", title: "Reason" },
      { dataKey: "contribution", title: "Contribution", inputType: "text-input", options: { type: "number" } }
    ],
    []
  );
  const rows = useDeepMemo(() => {
    if (isEmpty(reasonList)) return [];

    const alertList = !isEmpty(cutOrder?.orderConfig?.alerts) ? JSON.parse(cutOrder?.orderConfig?.alerts) : [];
    const cuParam = alertList?.find(e => e.parameter === AlertParemeter.CONSUMPTION_VARIATION.id);

    const contributionList = cuParam?.contributionList || [];
    return reasonList.map((reason, idx) => ({
      serial: idx + 1,
      reason,
      contribution: contributionList?.find(c => c.reason === reason)?.contribution
    }));
  }, [cutOrder, reasonList]);

  const onSubmit = (rows, _, onSubmitComplete) => {
    if (rows.some(row => isEmpty(row.contribution) || Number(row.contribution) === 0)) {
      dispatch(showNotification("Contribution is required against each reason", { type: "danger" }));
      onSubmitComplete(rows);
      return;
    }
    const totalContribution = rows.reduce((acc, row) => acc + Number(row.contribution), 0);
    if (toFixed(totalContribution) !== toFixed(cuDiff)) {
      dispatch(
        showNotification(
          `Total Contribution must be equal to consumption variance difference. Total Contribution = ${toFixedSigned(totalContribution)}`,
          { type: "danger" }
        )
      );
      onSubmitComplete(rows);
      return;
    }

    const contributionList = rows
      .filter(row => !isEmpty(row.contribution) && row.contribution !== 0)
      .map(e => ({ reason: e.reason, contribution: Number(e.contribution) }));
    const planningDto = { contributionList };

    const options = {
      url: `orders/${orderId}/markers/approval`,
      method: "put",
      data: planningDto,
      params: { action: "contribution", flowId: cutOrder?.id }
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Consumption variation contribution updated successfully."));
        onRefresh && onRefresh();
        onDrawerClose();
        onSubmitComplete && onSubmitComplete(rows);
      },
      onFailure: () => {
        onSubmitComplete(rows);
        onSubmitComplete && onSubmitComplete(rows);
      }
    });
  };

  return (
    <Drawer open={open || false} anchor="right" classes={{ paper: classes.drawerPaper }} onClose={onDrawerClose}>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onDrawerClose}>
          <ChevronRightIcon />
        </IconButton>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%" }}>
          <Typography variant="h5">{"Consumption Variance Analysis"}</Typography>
          <Typography variant="h5">{!isEmpty(cuDiff) ? `${toFixedSigned(cuDiff)} cm` : ""}</Typography>
        </div>
      </div>
      <Divider />

      <PerfectScrollbar>
        <MuiTable
          classes={{ table: classes.nowrapTable }}
          columns={columns}
          rows={rows}
          editable={cutOrder?.stateValue <= OState.CLOSURE_CONFIRMATION_PENDING.stateValue}
          editing
          onSubmit={onSubmit}
          fontSize={16}
          headerCellStyle={{ fontWeight: "bold" }}
          cellLength={100}
        />
      </PerfectScrollbar>
    </Drawer>
  );
};
